import React from "react";
import { Link } from "react-router-dom";
import AppStoreBadge from "../../../../assets/media/app-store-badge.svg";
import footerFollow from "../../../../assets/media/follow-your.svg";
import footerMobile from "../../../../assets/media/footer-mobile.svg";
import footer from "../../../../assets/media/footer.svg";
import GooglePlayBadge from "../../../../assets/media/play-store-badge.svg";
import { trackZostelEvent } from "../../../../utils/tracking";
import AppDownloadSection from "../Navigation/components/AppDownloadSection";
import {
  APP_FEATURES,
  APP_STORE_LINKS,
  legalLinks,
  navigationLinks,
  socialMediaPlatforms,
} from "./constants";

interface FooterProps {}

const Footer: React.FC<FooterProps> = () => {
  const handleNavigationClick = (eventName: string, trackEvent: string) => {
    gtag("event", eventName, { value: trackEvent });
    trackZostelEvent(eventName, { value: trackEvent });
  };
  return (
    <footer
      style={{
        paddingBottom: window.location.pathname === "/us" ? "80px" : "auto",
      }}
      className="my-10 sm:my-20"
    >
      <div className="relative ">
        <img
          src={footer}
          alt="Footer Background"
          className="w-full h-auto  object-cover mb-10 hidden lg:block"
        />
        <img
          src={footerFollow}
          alt="Footer Follow"
          className="w-36 h-auto object-cover mx-auto md:hidden"
        />
        <img
          src={footerMobile}
          alt="Footer Mobile"
          className="w-full h-auto object-cover my-10 px-6 mx-auto md:hidden"
        />
      </div>
      <div className="px-6 sm:px-28">
        <div className="flex flex-col lg:flex-row justify-between gap-8 lg:gap-12">
          <div className="w-full lg:w-144 mb-8 lg:mb-0 hidden sm:block">
            <div className="border border-light-gray rounded-2xl p-4 lg:p-6">
              <h3 className="text-xl lg:text-2xl font-medium mb-4">
                Download Zostel App
              </h3>

              <div className="flex flex-col lg:flex-row justify-between items-center gap-6 lg:gap-8">
                <div className="space-y-2 w-full lg:w-auto">
                  {APP_FEATURES.map((feature) => (
                    <div
                      key={feature.label}
                      className="flex items-center gap-4"
                    >
                      <span
                        role="img"
                        aria-label={feature.ariaLabel}
                        className="text-xl lg:text-2xl"
                      >
                        {feature.emoji}
                      </span>
                      <span className="text-sm lg:text-base">
                        {feature.label}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-center">
                  {/* QR Code - Hidden on mobile */}
                  <img
                    src={`${process.env.REACT_APP_IMAGE_PROXY_URL}/gallery/media/images/c47d2ce8-78c4-464b-b2c5-e2ef75aaf9c3_20250318025715.svg?w=320`}
                    alt="QR Code"
                    className="w-36 h-36 mb-1"
                  />

                  {/* Store badges */}
                  <div className="flex gap-2">
                    <a
                      href={APP_STORE_LINKS.ios}
                      className="bg-light-grey px-4 lg:px-6 py-2 rounded-lg"
                    >
                      <img
                        src={AppStoreBadge}
                        alt="App Store"
                        className="h-4"
                      />
                    </a>
                    <a
                      href={APP_STORE_LINKS.android}
                      className="bg-light-grey px-4 lg:px-6 py-2 rounded-lg"
                    >
                      <img
                        src={GooglePlayBadge}
                        alt="Play Store"
                        className="h-4"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="block sm:hidden mb-10">
            <AppDownloadSection />
          </div>

          <div className="grid grid-cols-2 lg:flex gap-6">
            {navigationLinks.map(({ title, links }) => (
              <div className="w-36 md:w-48" key={title}>
                <h4 className="font-medium mb-4 text-base">{title}</h4>
                <ul className="space-y-2">
                  {links.map(({ to, label }) => (
                    <li key={label}>
                      <Link
                        to={to}
                        className="text-sm hover:text-orange transition-colors duration-200"
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-8 lg:mt-12 pt-8 lg:pt-12 border-t border-light-gray">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 lg:gap-0">
            <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6 text-sm text-secondary">
              <span className="text-center lg:text-left">
                Zostel © 2025. All Rights Reserved
              </span>
              <div className="flex flex-wrap justify-center gap-4">
                {legalLinks.map(({ to, label }) => (
                  <Link
                    key={label}
                    to={to}
                    className="hover:text-orange transition-colors duration-200"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {socialMediaPlatforms.map((platform) => (
                <a
                  key={platform.name}
                  href={platform.link}
                  onClick={handleNavigationClick.bind(
                    null,
                    "Footer_Clicks",
                    platform.name
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mx-2"
                  style={
                    {
                      "--hover-color": "#ff6b00",
                    } as React.CSSProperties
                  }
                >
                  <platform.icon
                    width={24}
                    height={25}
                    className="social-icon"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
